import { useState, type FormEvent, type ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { journeySignInPath } from '@/lib/auth/post-auth-landing';
import { OTP_UI } from '@/lib/constants/otp';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import {
  RECOVERY_CODES_STRINGS as R,
  RECOVERY_SIGN_IN_STRINGS as RS,
  TWO_FACTOR_SETUP_STRINGS as S,
} from '@/lib/constants/two-factor-strings';
import { getPendingLogin } from '@/lib/auth/pre-auth';
import { peekPurchaseIntent } from '@/lib/auth/intent-storage';
import { completeLoginSession } from '@/lib/auth/complete-login-session';
import { AuthHero } from '@/components/common/AuthHero';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';
import { RecoveryCodesSaveCard } from '@/features/auth/components/RecoveryCodesSaveCard';
import { SetupRecoveryGateCard } from '@/features/auth/components/SetupRecoveryGateCard';
import { TotpConfirmCard } from '@/features/auth/components/TotpConfirmCard';
import { TotpScanCard } from '@/features/auth/components/TotpScanCard';
import { useConfirmTotpSetup, useTotpSetupStart } from '@/hooks/useTotpSetup';
import type { TotpSetupResult } from '@/api/auth.api';
import { getApiErrorCode, getApiErrorMessage, mapApiError } from '@/lib/utils/error.utils';

const CONFIRM_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.TOTP_CODE_INVALID]: S.ERROR_CODE_INVALID,
  [API_ERROR_CODES.MFA_SESSION_EXPIRED]: S.ERROR_SESSION_EXPIRED,
  [API_ERROR_CODES.TWO_FACTOR_LOCKED]: S.ERROR_LOCKED,
};

/** Why a recovery code entered at the setup gate was refused (FR-221). */
const GATE_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.RECOVERY_CODE_INVALID]: RS.ERROR_RECOVERY_INVALID,
  [API_ERROR_CODES.TWO_FACTOR_LOCKED]: S.ERROR_LOCKED,
};

const CODE_PATTERN = new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`);

/** Setup runs scan-and-confirm first, then shows the recovery codes. */
type SetupPhase = 'scan' | 'codes';

/** Everything that varies by phase, resolved once. */
interface PhaseView {
  heading: ReactNode;
  subtitle: string;
  body: ReactNode;
}

/** The scan phase's hero heading, with the highlighted product term. */
const SCAN_HEADING = (
  <>
    {S.HEADING_PREFIX}
    <span className="text-green">{S.HEADING_HIGHLIGHT}</span>
  </>
);

/** Scan (or type the key) and confirm the first code; owns the code and the confirm request. */
function ScanAndConfirmStep({
  preAuthToken,
  onConfirmed,
}: {
  preAuthToken: string;
  onConfirmed: (result: TotpSetupResult) => void;
}) {
  const [recoveryCode, setRecoveryCode] = useState<string>();
  const start = useTotpSetupStart(preAuthToken, recoveryCode);
  const confirm = useConfirmTotpSetup(preAuthToken);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();

  // An abandoned re-scan: setup waits for a recovery code (FR-221). The gate
  // stays up while a code entered there is checked or refused.
  const gateRequired = getApiErrorCode(start.error) === API_ERROR_CODES.RECOVERY_CODE_REQUIRED;
  if (gateRequired || (recoveryCode && !start.data)) {
    return (
      <SetupRecoveryGateCard
        onSubmit={setRecoveryCode}
        isSubmitting={Boolean(recoveryCode) && start.isFetching}
        submitError={
          recoveryCode && start.isError
            ? getApiErrorMessage(start.error, mapApiError(start.error, GATE_ERROR_BY_CODE, S.LOAD_FAILED))
            : undefined
        }
      />
    );
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!CODE_PATTERN.test(code)) {
      setCodeError(S.CODE_FORMAT_ERROR);
      return;
    }
    setCodeError(undefined);
    confirm.mutate(code, { onSuccess: onConfirmed });
  };

  const submitError = confirm.isError
    ? getApiErrorMessage(
        confirm.error,
        mapApiError(confirm.error, CONFIRM_ERROR_BY_CODE, S.SETUP_FAILED),
      )
    : undefined;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
      <TotpScanCard start={start} />
      <TotpConfirmCard
        code={code}
        onCodeChange={setCode}
        codeError={codeError}
        submitError={submitError}
        isSubmitting={confirm.isPending}
        canSubmit={Boolean(start.data)}
      />
    </form>
  );
}

/**
 * Authenticator-app setup during sign-in (MYN-241), for a login that answered
 * `setupRequired`. Scan (or type the key), confirm the first code, save the
 * recovery codes, then the session starts. Transition-only like the code step:
 * no pending login means back to sign-in.
 */
export function TwoFactorSetupPage() {
  // Read once per mount, as on the code step: a re-render after the session
  // completed must not find the cleared holder and bounce to /login (MYN-272).
  const [pending] = useState(getPendingLogin);
  const preAuthToken = pending?.challenge.preAuthToken ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<TotpSetupResult | null>(null);

  if (!preAuthToken || !pending?.challenge.setupRequired) {
    return <Navigate to={journeySignInPath()} replace />;
  }

  const finish = () => {
    if (result) completeLoginSession(result, { queryClient, navigate, returnTo: pending.returnTo });
  };

  const views: Record<SetupPhase, PhaseView> = {
    scan: {
      heading: SCAN_HEADING,
      subtitle: S.SUBTITLE,
      body: <ScanAndConfirmStep preAuthToken={preAuthToken} onConfirmed={setResult} />,
    },
    codes: {
      heading: R.HEADING,
      subtitle: R.DESCRIPTION,
      body: (
        <RecoveryCodesSaveCard
          codes={result?.recoveryCodes ?? []}
          onContinue={finish}
          continueLabel={peekPurchaseIntent() ? R.CONTINUE_JOURNEY : R.CONTINUE}
        />
      ),
    },
  };
  const view = views[result ? 'codes' : 'scan'];

  return (
    <div className="bg-page-bg flex flex-col items-center px-4 py-16">
      {peekPurchaseIntent() && <IssueReportJourneySteps stage="secure" className="mb-10" />}
      <AuthHero
        badgeIcon={<ShieldCheck size={14} />}
        badgeText={S.BADGE}
        heading={view.heading}
        subtitleClassName="max-w-2xl"
        subtitle={<p>{view.subtitle}</p>}
      />
      <div className="mt-10 flex w-full max-w-3xl flex-col gap-6">{view.body}</div>
    </div>
  );
}
